/**
 * Parent-family mapping for the Intelligence page category matrix.
 *
 * Collapses the 40+ granular `categories.slug` values into 10 display
 * families + an `Other` bucket. Locked against the live taxonomy
 * validated on 2026-04-14. Every family has meaningful project counts
 * in the 40+/30-39/20-29/<20 score bands (a few low-sample cells get
 * flagged at the UI layer via the `n < 5` rule).
 *
 * Design notes:
 *
 * - `Other` is kept visible rather than widened into Tools & Exchange
 *   so categorization gaps surface explicitly instead of being hidden.
 *   As the category taxonomy expands (tracked in TASKS.md), projects
 *   should migrate out of Other into their proper family.
 * - `stablecoin` currently lives under DeFi because most stablecoin
 *   projects in the corpus are lending/DEX-adjacent. If the mapping
 *   changes for editorial reasons (Payments is also defensible) it's
 *   a one-line edit here.
 * - `bitcoin-ecosystem` lives under Infra — the corpus projects tagged
 *   this way are mostly L1/L2 infrastructure plays rather than DeFi.
 *
 * When adding a new `categories.slug` row, add it here too. The
 * `family-mapping.test.ts` file asserts every live `categories.slug`
 * is accounted for (either in a family or explicitly in the
 * `UNMAPPED_FALLBACK` allowlist).
 */

export const CATEGORY_FAMILIES = [
  "DeFi",
  "Infra",
  "AI & DePIN",
  "Payments",
  "Tools & Exchange",
  "Gaming & NFT",
  "Prediction & Betting",
  "Privacy & Identity",
  "SocialFi",
  "RWA",
  "Other",
] as const;

export type CategoryFamily = (typeof CATEGORY_FAMILIES)[number];

/**
 * Slug → parent family. Slugs not in this map fall through to `Other`.
 * Order does not matter for lookup — it only affects the SQL CASE
 * expression generated below.
 */
export const SLUG_TO_FAMILY: Record<string, CategoryFamily> = {
  // ── DeFi ──────────────────────────────────────────────────────────
  dex: "DeFi",
  "dex-aggregator": "DeFi",
  lending: "DeFi",
  "cefi-lending": "DeFi",
  "liquid-staking": "DeFi",
  "yield-aggregator": "DeFi",
  perpetuals: "DeFi",
  options: "DeFi",
  derivatives: "DeFi",
  "synthetic-assets": "DeFi",
  restaking: "DeFi",
  mev: "DeFi",
  defi: "DeFi",
  stablecoin: "DeFi",
  cdp: "DeFi",

  // ── Infra ─────────────────────────────────────────────────────────
  l1: "Infra",
  l2: "Infra",
  "modular-da": "Infra",
  bridge: "Infra",
  interop: "Infra",
  storage: "Infra",
  oracle: "Infra",
  "account-abstraction": "Infra",
  zk: "Infra",
  infrastructure: "Infra",
  "bitcoin-ecosystem": "Infra",

  // ── AI & DePIN ────────────────────────────────────────────────────
  ai: "AI & DePIN",
  "ai-agent": "AI & DePIN",
  depin: "AI & DePIN",

  // ── Payments ──────────────────────────────────────────────────────
  payments: "Payments",

  // ── Tools & Exchange ──────────────────────────────────────────────
  "dev-tools": "Tools & Exchange",
  wallet: "Tools & Exchange",
  launchpad: "Tools & Exchange",
  exchange: "Tools & Exchange",

  // ── Gaming & NFT ──────────────────────────────────────────────────
  gaming: "Gaming & NFT",
  nft: "Gaming & NFT",
  "nft-marketplace": "Gaming & NFT",
  nftfi: "Gaming & NFT",

  // ── Prediction & Betting ──────────────────────────────────────────
  "prediction-market": "Prediction & Betting",
  gambling: "Prediction & Betting",

  // ── Privacy & Identity ────────────────────────────────────────────
  privacy: "Privacy & Identity",
  identity: "Privacy & Identity",

  // ── SocialFi ──────────────────────────────────────────────────────
  social: "SocialFi",
  "creator-economy": "SocialFi",
  dao: "SocialFi",
  meme: "SocialFi",

  // ── RWA ───────────────────────────────────────────────────────────
  rwa: "RWA",
};

/**
 * Score band bucketing (validated 2026-04-14 against live distribution:
 * 116 / 138 / 148 / 98 projects). The originally-planned
 * 80+/70-79/60-69/<60 split put 493 / 500 projects into the lowest
 * bucket and was unusable.
 */
export const SCORE_BANDS = ["40+", "30-39", "20-29", "<20"] as const;
export type ScoreBand = (typeof SCORE_BANDS)[number];

/**
 * Build the `CASE` expression that maps `categories.slug` → parent
 * family for use in raw SQL queries. Keeps the family mapping in
 * TypeScript (one source of truth) while still letting aggregates
 * do the bucketing server-side for efficiency.
 *
 * Returns a SQL fragment like:
 *   CASE c.slug
 *     WHEN 'dex' THEN 'DeFi'
 *     ...
 *     ELSE 'Other'
 *   END
 *
 * Caller must trust that family names contain no SQL metacharacters.
 * `CATEGORY_FAMILIES` is a compile-time constant so this is safe.
 */
export function buildFamilyCaseSql(aliasColumn: string = "c.slug"): string {
  const whens = Object.entries(SLUG_TO_FAMILY)
    .map(([slug, family]) => `    WHEN '${slug}' THEN '${family}'`)
    .join("\n");
  return `CASE ${aliasColumn}\n${whens}\n    ELSE 'Other'\n  END`;
}

/**
 * Build the `CASE` expression that buckets `current_alpha_score` into
 * the four locked score bands. Caller must trust that band labels
 * contain no SQL metacharacters (they don't — they're all `\w+[+-<]`).
 */
export function buildScoreBandCaseSql(scoreColumn: string = "p.current_alpha_score"): string {
  return `CASE
    WHEN ${scoreColumn} >= 40 THEN '40+'
    WHEN ${scoreColumn} >= 30 THEN '30-39'
    WHEN ${scoreColumn} >= 20 THEN '20-29'
    ELSE '<20'
  END`;
}
